import { ApiProperty } from '@nestjs/swagger'
import { IsOptional, IsString, IsInt, Min, Max } from 'class-validator'
import { Type } from 'class-transformer'

// ============================================================================
// Query DTOs
// ============================================================================

export class CursorPaginationQueryDto {
  @ApiProperty({ required: false, description: 'Cursor de pagination opaque' })
  @IsOptional()
  @IsString()
  cursor?: string

  @ApiProperty({
    required: false,
    default: 20,
    minimum: 1,
    maximum: 100,
    description: "Nombre d'éléments par page",
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(100)
  limit?: number = 20

  @ApiProperty({ required: false, description: 'Recherche textuelle' })
  @IsOptional()
  @IsString()
  q?: string
}

// ============================================================================
// Response DTOs - Establishment Members (agrégation School + User)
// ============================================================================

/**
 * Rôle simplifié pour l'affichage
 */
export interface RoleSummary {
  id: string
  code: string
  name: string
  description?: string
  permissions?: string
}

/**
 * Établissement simplifié pour l'affichage
 */
export interface EstablishmentSummary {
  id: string
  name: string
  uai?: string
}

/**
 * Capabilities utilisateur
 */
import type { UserCapabilities } from '@sphere/user/domain'

/**
 * Informations utilisateur complètes
 */
export interface UserInfo {
  id: string
  email: string | null
  username: string
  firstName: string
  lastName: string
  displayName: string
  avatarUrl: string | null
  status: string
  locale: string
  theme: string
  lastSeenAt: string | null
  createdAt: string
  updatedAt: string
  capabilities: UserCapabilities
}

/**
 * Niveau (level) simplifié
 */
export interface LevelSummary {
  code: string
  label: string
}

/**
 * Classe simplifiée avec niveau
 */
export interface ClassSummary {
  id: string
  name: string
  establishmentId: string
  level?: LevelSummary
}

/**
 * Groupe simplifié
 */
export interface GroupSummary {
  id: string
  name: string
  establishmentId: string
}

/**
 * Membership de classe pour un user
 */
export interface UserClassMembership {
  id: string
  classId: string
  roleId: string
  joinedAt: string
  class: ClassSummary
  role?: { id: string; code: string; name: string }
}

/**
 * Membership de groupe pour un user
 */
export interface UserGroupMembership {
  id: string
  groupId: string
  joinedAt: string
  group: GroupSummary
}

/**
 * Membership de classe avec données utilisateur enrichies
 * Résultat de l'agrégation School Service + User Service
 */
export interface ClassMemberWithUser {
  // Données du membership (de School Service)
  id: string
  classId: string
  userId: string
  roleId: string
  isHead: boolean
  joinedAt: string
  leftAt: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string

  // Relations enrichies
  class: ClassSummary
  role: RoleSummary

  // Données utilisateur (de User Service)
  user: UserInfo
}

/**
 * Membership de groupe avec données utilisateur enrichies
 */
export interface GroupMemberWithUser {
  // Données du membership (de School Service)
  id: string
  groupId: string
  studentUserId: string
  originClassId: string
  joinedAt: string
  leftAt: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string

  // Relations enrichies
  group: GroupSummary
  originClass?: ClassSummary

  // Données utilisateur (de User Service)
  user: UserInfo
}

/**
 * Membership d'établissement avec données utilisateur enrichies
 * Résultat de l'agrégation School Service + User Service
 */
export interface EstablishmentMemberWithUser {
  // Données du membership (de School Service)
  id: string
  establishmentId: string
  userId: string
  roleId: string
  joinedAt: string
  leftAt: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string

  // Relations enrichies
  establishment: EstablishmentSummary
  role: RoleSummary

  // Classes et groupes (de School Service)
  classes: UserClassMembership[]
  groups: UserGroupMembership[]

  // Données utilisateur (de User Service)
  user: UserInfo
}
